import React, { createContext, useContext, useEffect, useCallback, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useConsent } from './ConsentContext';
import { OpenPanel } from '@openpanel/web';

const AnalyticsContext = createContext(null);

// LocalStorage keys for persistent user identification
const ANONYMOUS_ID_KEY = 'op_anonymous_id';
const USER_ID_KEY = 'op_user_id';
const ALIASED_KEY = 'op_user_aliased';
const isProductionAnalyticsHost = () =>
    ['anasarabic.com', 'www.anasarabic.com'].includes(window.location.hostname);

export const useAnalytics = () => {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within an AnalyticsProvider');
    }
    return context;
};

export const AnalyticsProvider = ({ children }) => {
    const location = useLocation();
    const { hasConsent, consentRequired } = useConsent();
    const [isReady, setIsReady] = useState(false);
    const openPanelRef = useRef(null);
    const utmParamsRef = useRef(null);
    const adClickIdsRef = useRef(null);
    const lastPageViewRef = useRef(null);

    // Initialize OpenPanel SDK
    useEffect(() => {
        if (!isProductionAnalyticsHost()) return;
        const clientId = process.env.REACT_APP_OPENPANEL_CLIENT_ID;

        if (!clientId) {
            console.warn('[Analytics] OpenPanel client ID not configured');
            return;
        }

        try {
            const op = new OpenPanel({
                clientId: clientId,
                apiUrl: process.env.REACT_APP_OPENPANEL_API_URL || 'https://opapi.anasarabic.com',
                trackScreenViews: true,
                trackOutgoingLinks: true,
                trackAttributes: true,
            });

            // Persistent anonymous ID for cross-session tracking
            // This ensures the same visitor is tracked across browser sessions
            let anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
            if (!anonId) {
                // Generate a new anonymous ID if none exists
                anonId = crypto.randomUUID ? crypto.randomUUID() :
                    'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
                localStorage.setItem(ANONYMOUS_ID_KEY, anonId);
                console.log('[Analytics] 🆔 Generated new anonymous ID:', anonId);
            } else {
                console.log('[Analytics] 🆔 Restored anonymous ID:', anonId);
            }

            // Set the anonymous ID in OpenPanel
            if (typeof op.setAnonymousId === 'function') {
                op.setAnonymousId(anonId);
            } else {
                console.warn('[Analytics] setAnonymousId not supported in this SDK version');
            }

            // Check if user was previously authenticated (for logging only)
            const storedUserId = localStorage.getItem(USER_ID_KEY);
            if (storedUserId) {
                console.log('[Analytics] 👤 Previous user session found:', storedUserId);
            }

            openPanelRef.current = op;
            console.log('[Analytics] ✅ OpenPanel SDK initialized with API:', process.env.REACT_APP_OPENPANEL_API_URL || 'self-hosted');
            setIsReady(true);
        } catch (error) {
            console.error('[Analytics] Failed to initialize OpenPanel:', error);
        }
    }, []);

    // Capture UTM params and ad click IDs on mount (persist across navigation)
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        // Only capture if not already captured
        if (!utmParamsRef.current) {
            const utmSource = params.get('utm_source');
            const utmMedium = params.get('utm_medium');
            const utmCampaign = params.get('utm_campaign');

            if (utmSource || utmMedium || utmCampaign) {
                utmParamsRef.current = {
                    utm_source: utmSource || undefined,
                    utm_medium: utmMedium || undefined,
                    utm_campaign: utmCampaign || undefined,
                    utm_content: params.get('utm_content') || undefined,
                    utm_term: params.get('utm_term') || undefined,
                };
                // Store in sessionStorage for persistence
                sessionStorage.setItem('utm_params', JSON.stringify(utmParamsRef.current));
            } else {
                // Try to load from sessionStorage
                const stored = sessionStorage.getItem('utm_params');
                if (stored) {
                    utmParamsRef.current = JSON.parse(stored);
                }
            }
        }

        if (!adClickIdsRef.current) {
            const fbclid = params.get('fbclid');
            const gclid = params.get('gclid');

            if (fbclid || gclid) {
                adClickIdsRef.current = {
                    fbclid: fbclid || undefined,
                    gclid: gclid || undefined,
                    gbraid: params.get('gbraid') || undefined,
                    wbraid: params.get('wbraid') || undefined,
                };
                sessionStorage.setItem('ad_click_ids', JSON.stringify(adClickIdsRef.current));
            } else {
                const stored = sessionStorage.getItem('ad_click_ids');
                if (stored) {
                    adClickIdsRef.current = JSON.parse(stored);
                }
            }
        }
    }, []);

    // Get UTM parameters
    const getUtmParams = useCallback(() => {
        return utmParamsRef.current || {};
    }, []);

    // Get ad platform click IDs
    const getAdClickIds = useCallback(() => {
        return adClickIdsRef.current || {};
    }, []);

    // Classify traffic source
    const getTrafficSource = useCallback(() => {
        const adClickIds = getAdClickIds();
        const utmParams = getUtmParams();

        // If we have any ad click ID, it's paid traffic
        if (adClickIds.fbclid || adClickIds.gclid || adClickIds.gbraid || adClickIds.wbraid) {
            return 'paid';
        }

        // Check UTM source for paid indicators
        const paidSources = ['facebook', 'instagram', 'google', 'youtube', 'meta', 'fb', 'ig'];
        if (utmParams.utm_source && paidSources.some(s =>
            utmParams.utm_source.toLowerCase().includes(s)
        )) {
            return 'paid';
        }

        return 'organic';
    }, [getAdClickIds, getUtmParams]);

    const eventQueue = useRef([]);

    // Core tracking wrapper with consent check
    const trackEvent = useCallback((eventName, properties = {}) => {
        if (!isProductionAnalyticsHost()) return;
        if (consentRequired && !hasConsent) {
            console.warn('[Analytics] Consent not granted, skipping event:', eventName);
            return;
        }

        if (openPanelRef.current) {
            const enrichedProperties = {
                ...properties,
                ...getUtmParams(),
                ...getAdClickIds(),
                traffic_source: getTrafficSource(),
                page_url: window.location.href,
                page_path: window.location.pathname,
                timestamp: new Date().toISOString(),
            };

            console.log('[Analytics] ⚡ Tracking event:', eventName, enrichedProperties);
            openPanelRef.current.track(eventName, enrichedProperties);
        } else {
            console.log('[Analytics] ⏳ OpenPanel not ready, queueing event:', eventName);
            eventQueue.current.push({ type: 'track', name: eventName, properties });
        }
    }, [consentRequired, hasConsent, getUtmParams, getAdClickIds, getTrafficSource]);

    // Page view tracking
    const trackPageView = useCallback((category, name, properties = {}) => {
        if (!isProductionAnalyticsHost()) return;
        if (consentRequired && !hasConsent) {
            console.warn('[Analytics] Consent not granted, skipping page view');
            return;
        }

        if (openPanelRef.current) {
            // Consent/context updates can rerun the route effect. Count a
            // custom page view once per navigation, not once per rerender.
            const pageViewKey = `${location.key}:${category}:${name}`;
            if (lastPageViewRef.current === pageViewKey) return;
            const pageProperties = {
                ...properties,
                ...getUtmParams(),
                ...getAdClickIds(),
                traffic_source: getTrafficSource(),
                category,
                page_name: name,
            };

            console.log('[Analytics] 📄 Page view:', category, name, pageProperties);
            openPanelRef.current.track('page_view', pageProperties);
            lastPageViewRef.current = pageViewKey;
        }
    }, [consentRequired, hasConsent, getUtmParams, getAdClickIds, getTrafficSource, location.key]);

    // Track page views on route change
    useEffect(() => {
        const path = location.pathname;
        let category = 'General';
        let name = path;

        if (path === '/' || path === '') {
            category = 'Landing';
            name = 'Home';
        } else if (path.startsWith('/signup')) {
            category = 'Conversion';
            name = 'Signup';
        } else if (path.startsWith('/login')) {
            category = 'Auth';
            name = 'Login';
        } else if (path.startsWith('/level-test')) {
            category = 'Conversion';
            name = 'Level Test';
        } else if (path.startsWith('/become-partner')) {
            category = 'B2B';
            name = 'Partner Application';
        } else if (path.startsWith('/pricing') || path.startsWith('/subscribe')) {
            category = 'Conversion';
            name = 'Pricing';
        } else if (path.startsWith('/dashboard')) {
            category = 'App';
            name = 'Dashboard';
        } else if (path.startsWith('/levels') || path.startsWith('/courses')) {
            category = 'App';
            name = 'Courses';
        } else if (path.startsWith('/special-offer')) {
            category = 'Recovery';
            name = 'Special Offer';
        }

        trackPageView(category, name);
    }, [location.pathname, trackPageView]);

    // Identify user
    const identify = useCallback((userId, traits = {}) => {
        if (openPanelRef.current) {
            const enrichedTraits = {
                ...traits,
                ...getUtmParams(),
                traffic_source: getTrafficSource(),
                identified_at: new Date().toISOString(),
            };

            console.log('[Analytics] 👤 Identifying user:', userId, enrichedTraits);
            openPanelRef.current.identify({
                profileId: userId,
                ...enrichedTraits,
            });

            // Store user ID for cross-session identification
            localStorage.setItem(USER_ID_KEY, userId);
        }
    }, [getUtmParams, getTrafficSource]);

    // Alias method - merges anonymous profile into authenticated user profile
    // CRITICAL: Call this BEFORE identify when transitioning from anonymous to authenticated
    const alias = useCallback((previousId, newId) => {
        if (openPanelRef.current && previousId && newId && previousId !== newId) {
            console.log('[Analytics] 🔗 Aliasing:', previousId, '→', newId);
            openPanelRef.current.alias({
                profileId: newId,
                alias: previousId,
            });
            // Mark that we've aliased this user to prevent duplicate alias calls
            localStorage.setItem(ALIASED_KEY, newId);
        }
    }, []);

    // Reset user (on logout)
    const reset = useCallback(() => {
        if (openPanelRef.current) {
            console.log('[Analytics] 🔄 Resetting user identity');
            openPanelRef.current.clear();

            // Clear stored user ID and alias state, but keep anonymous ID
            // so we can track them as an anonymous user again
            localStorage.removeItem(USER_ID_KEY);
            localStorage.removeItem(ALIASED_KEY);

            // Generate a new anonymous ID for the logged-out session
            const newAnonId = crypto.randomUUID ? crypto.randomUUID() :
                'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
            localStorage.setItem(ANONYMOUS_ID_KEY, newAnonId);
            openPanelRef.current.setAnonymousId(newAnonId);
            console.log('[Analytics] 🆔 Generated new anonymous ID for logged-out session:', newAnonId);
        }
    }, []);

    // Flush event queue when SDK is ready (placed after trackEvent and identify are defined)
    useEffect(() => {
        if (isReady && eventQueue.current.length > 0) {
            console.log(`[Analytics] 🔄 Flushing ${eventQueue.current.length} queued events`);
            eventQueue.current.forEach(item => {
                if (item.type === 'track') {
                    trackEvent(item.name, item.properties);
                } else if (item.type === 'identify') {
                    identify(item.userId, item.traits);
                }
            });
            eventQueue.current = [];
        }
    }, [isReady, trackEvent, identify]);

    // ==========================================
    // SCENARIO 1 & 3: Happy Path Events
    // ==========================================

    const trackSignupStarted = useCallback((signupSource = 'direct') => {
        trackEvent('signup_started', {
            signup_source: signupSource,
            funnel_stage: 'top',
        });
    }, [trackEvent]);

    const trackSignupFormStep = useCallback((step, fieldCompleted) => {
        trackEvent('signup_form_step', {
            step,
            field_completed: fieldCompleted,
            funnel_stage: 'middle',
        });
    }, [trackEvent]);

    const trackSignup = useCallback((method, userId, userEmail, firstName = null, lastName = null, phone = null, whatsappVerified = false) => {
        // CRITICAL: Alias anonymous → authenticated BEFORE identify
        // This merges all pre-signup anonymous activity to this user
        const anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
        if (anonId && anonId !== userId) {
            alias(anonId, userId);
        }

        // Now identify the user with their traits
        identify(userId, {
            email: userEmail,
            firstName: firstName,
            lastName: lastName,
            phone: phone,
            whatsapp_verified: whatsappVerified,
            signup_method: method,
            subscription_status: 'trial',
            trial_start_date: new Date().toISOString(),
            segment: 'parent_b2c',
        });

        // Track the signup event
        trackEvent('signup_completed', {
            signup_method: method,
            funnel_stage: 'conversion',
            user_id: userId,
            whatsapp_verified: whatsappVerified,
        });
    }, [alias, identify, trackEvent]);

    const trackLogin = useCallback((method, userId, userEmail, firstName = null, lastName = null) => {
        // CRITICAL: Alias anonymous → authenticated BEFORE identify
        // This handles returning users or users on new devices
        // Merges any anonymous activity from this session with their existing profile
        const anonId = localStorage.getItem(ANONYMOUS_ID_KEY);
        const wasAliased = localStorage.getItem(ALIASED_KEY) === userId;

        // Only alias if not already aliased for this user in this session
        if (anonId && anonId !== userId && !wasAliased) {
            alias(anonId, userId);
        }

        // Identify the user with their traits
        identify(userId, {
            email: userEmail,
            firstName: firstName,
            lastName: lastName,
            last_login: new Date().toISOString(),
        });

        // Track the login event
        trackEvent('login_completed', {
            method,
            user_id: userId,
        });
    }, [alias, identify, trackEvent]);

    const trackLogout = useCallback(() => {
        trackEvent('logout');
        reset();
    }, [trackEvent, reset]);

    // ==========================================
    // WhatsApp Verification Events
    // ==========================================

    const trackWhatsAppOTPSent = useCallback((phone) => {
        trackEvent('whatsapp_otp_sent', {
            phone_masked: phone ? `***${phone.slice(-4)}` : null,
            funnel_stage: 'verification',
        });
    }, [trackEvent]);

    const trackWhatsAppVerified = useCallback((userId) => {
        trackEvent('whatsapp_verified', {
            user_id: userId,
            funnel_stage: 'verification',
        });
    }, [trackEvent]);

    const trackWhatsAppSkipped = useCallback((userId) => {
        trackEvent('whatsapp_skipped', {
            user_id: userId,
            funnel_stage: 'verification',
        });
    }, [trackEvent]);

    // ==========================================
    // SCENARIO 2 & 4: Abandonment Path Events
    // ==========================================

    const trackSignupAbandoned = useCallback((data = {}) => {
        trackEvent('signup_abandoned', {
            ...data,
            funnel_stage: 'abandoned',
        });
    }, [trackEvent]);

    const trackFormAbandoned = useCallback((formName, step, lastFieldCompleted, partialData = {}) => {
        // If we have email, identify for retargeting
        if (partialData.email) {
            identify(`anonymous_${Date.now()}`, {
                email: partialData.email,
                phone: partialData.phone,
                form_abandoned: true,
                segment: 'lead_abandoned',
            });
        }

        trackEvent('form_abandoned', {
            form_name: formName,
            step,
            last_field_completed: lastFieldCompleted,
            has_email: !!partialData.email,
            has_phone: !!partialData.phone,
            funnel_stage: 'abandoned',
        });
    }, [identify, trackEvent]);

    // ==========================================
    // Exit-Intent Events
    // ==========================================

    const trackExitIntentTriggered = useCallback((page, scrollDepth) => {
        trackEvent('exit_intent_triggered', {
            page,
            scroll_depth: scrollDepth,
            traffic_source: getTrafficSource(),
        });
    }, [trackEvent, getTrafficSource]);

    const trackExitPopupShown = useCallback((offerType) => {
        trackEvent('exit_popup_shown', {
            offer_type: offerType,
        });
    }, [trackEvent]);

    const trackExitPopupConverted = useCallback((leadMagnet, email, phone, name) => {
        trackEvent('exit_popup_converted', {
            lead_magnet: leadMagnet,
            email: email,
            phone: phone,
            name: name,
            has_whatsapp: !!phone,
            has_email: !!email,
            funnel_stage: 'lead_captured',
        });
    }, [trackEvent]);

    const trackExitPopupDismissed = useCallback(() => {
        trackEvent('exit_popup_dismissed', {
            funnel_stage: 'abandoned',
        });
    }, [trackEvent]);

    // ==========================================
    // Level Test Events
    // ==========================================

    const trackAssessmentStarted = useCallback((assessmentType) => {
        if (assessmentType === 'level_test') {
            trackEvent('level_test_started', {
                assessment_type: 'level_test',
                funnel_stage: 'engagement',
            });
        } else {
            trackEvent('assessment_started', {
                assessment_type: assessmentType,
            });
        }
    }, [trackEvent]);

    const trackAssessmentCompleted = useCallback((assessmentData) => {
        const { assessmentType, score, level, timeSpent } = assessmentData;

        if (assessmentType === 'level_test') {
            trackEvent('level_test_completed', {
                level_result: level,
                test_score: score,
                time_spent_seconds: timeSpent,
                funnel_stage: 'engagement',
            });
        } else {
            trackEvent('assessment_completed', assessmentData);
        }
    }, [trackEvent]);

    const trackLevelTestAbandoned = useCallback((data = {}) => {
        trackEvent('level_test_abandoned', {
            ...data,
            funnel_stage: 'abandoned',
        });
    }, [trackEvent]);

    // ==========================================
    // Trial & Subscription Events
    // ==========================================

    const trackTrialStarted = useCallback((data = {}) => {
        trackEvent('trial_started', {
            ...data,
            funnel_stage: 'trial',
        });
    }, [trackEvent]);

    const trackTrialEngagement = useCallback((data = {}) => {
        trackEvent('trial_engagement', {
            ...data,
            funnel_stage: 'trial',
        });
    }, [trackEvent]);

    const trackSubscriptionStarted = useCallback((subscriptionData) => {
        const { tier, amount, currency, interval } = subscriptionData;
        trackEvent('subscription_started', {
            tier,
            amount,
            currency,
            interval,
            funnel_stage: 'conversion',
        });
    }, [trackEvent]);

    const trackSubscriptionCancelled = useCallback((tier) => {
        trackEvent('subscription_cancelled', {
            tier,
            funnel_stage: 'churn',
        });
    }, [trackEvent]);

    const trackSubscriptionRenewed = useCallback((subscriptionData) => {
        const { tier, amount, currency } = subscriptionData;
        trackEvent('subscription_renewed', {
            tier,
            amount,
            currency,
            funnel_stage: 'retention',
        });
    }, [trackEvent]);

    const trackSubscriptionActivated = useCallback((data = {}) => {
        trackEvent('subscription_activated', {
            ...data,
            funnel_stage: 'conversion',
        });
    }, [trackEvent]);

    // ==========================================
    // Checkout Events
    // ==========================================

    const trackCheckoutStarted = useCallback((data = {}) => {
        trackEvent('checkout_started', {
            ...data,
            funnel_stage: 'checkout',
        });
    }, [trackEvent]);

    const trackCheckoutAbandoned = useCallback((data = {}) => {
        trackEvent('checkout_abandoned', {
            ...data,
            funnel_stage: 'abandoned',
        });
    }, [trackEvent]);

    const trackPaymentCompleted = useCallback((data = {}) => {
        trackEvent('payment_completed', {
            ...data,
            funnel_stage: 'conversion',
        });
    }, [trackEvent]);

    // ==========================================
    // SCENARIO 5: Win-back Events
    // ==========================================

    const trackWinbackTriggered = useCallback((data = {}) => {
        trackEvent('winback_triggered', {
            ...data,
            discount_offered: true,
            funnel_stage: 'winback',
        });
    }, [trackEvent]);

    const trackDiscountApplied = useCallback((discountCode, discountPercent) => {
        trackEvent('discount_applied', {
            discount_code: discountCode,
            discount_percent: discountPercent,
            funnel_stage: 'winback',
        });
    }, [trackEvent]);

    // ==========================================
    // SCENARIO 6: B2B Partner Events
    // ==========================================

    const trackPartnerApplicationStarted = useCallback(() => {
        trackEvent('partner_application_started', {
            segment: 'institution_b2b',
            funnel_stage: 'b2b_lead',
        });
    }, [trackEvent]);

    const trackPartnerApplicationSubmitted = useCallback((institutionData = {}) => {
        trackEvent('partner_application_submitted', {
            ...institutionData,
            segment: 'institution_b2b',
            funnel_stage: 'b2b_lead',
        });
    }, [trackEvent]);

    // ==========================================
    // Account & Demo Events
    // ==========================================

    const trackAccountActivationEmailSent = useCallback((data = {}) => {
        trackEvent('account_activation_email_sent', data);
    }, [trackEvent]);

    const trackAccountActivated = useCallback((data = {}) => {
        trackEvent('account_activated', data);
    }, [trackEvent]);

    const trackDemoRequested = useCallback((userId) => {
        trackEvent('demo_requested', { user_id: userId });
    }, [trackEvent]);

    const trackDemoActivated = useCallback((userId) => {
        identify(userId, {
            demo_status: 'active',
        });
        trackEvent('demo_activated', { user_id: userId });
    }, [identify, trackEvent]);

    const trackUpgradePromptShown = useCallback((source) => {
        trackEvent('upgrade_prompt_shown', { source });
    }, [trackEvent]);

    const trackUpgradeButtonClicked = useCallback((source) => {
        trackEvent('upgrade_button_clicked', { source });
    }, [trackEvent]);

    // ==========================================
    // Course & Lesson Events
    // ==========================================

    const trackCourseViewed = useCallback((courseData) => {
        trackEvent('course_viewed', courseData);
    }, [trackEvent]);

    const trackLessonStarted = useCallback((lessonData) => {
        trackEvent('lesson_started', lessonData);
    }, [trackEvent]);

    const trackLessonCompleted = useCallback((lessonData) => {
        trackEvent('lesson_completed', lessonData);
    }, [trackEvent]);

    // ==========================================
    // Re-engagement Events (for automation)
    // ==========================================

    const trackEmailSent = useCallback((data = {}) => {
        trackEvent('email_sent', data);
    }, [trackEvent]);

    const trackEmailOpened = useCallback((data = {}) => {
        trackEvent('email_opened', data);
    }, [trackEvent]);

    const trackEmailClicked = useCallback((data = {}) => {
        trackEvent('email_clicked', data);
    }, [trackEvent]);

    const trackWhatsappSent = useCallback((data = {}) => {
        trackEvent('whatsapp_sent', data);
    }, [trackEvent]);

    const trackWhatsappRead = useCallback((data = {}) => {
        trackEvent('whatsapp_read', data);
    }, [trackEvent]);

    const trackWhatsappReplied = useCallback((data = {}) => {
        trackEvent('whatsapp_replied', data);
    }, [trackEvent]);

    // ==========================================
    // Scroll Depth Tracking (for exit-intent)
    // ==========================================

    const trackScrollDepth = useCallback((depth, page) => {
        trackEvent('scroll_depth', {
            depth_percentage: depth,
            page,
        });
    }, [trackEvent]);

    // ==========================================
    // User Engagement Events
    // ==========================================

    const trackButtonClick = useCallback((buttonName, context = {}) => {
        trackEvent('button_clicked', { button_name: buttonName, ...context });
    }, [trackEvent]);

    const trackFormSubmitted = useCallback((formName, success = true, errorMessage = null) => {
        trackEvent('form_submitted', {
            form_name: formName,
            success,
            error_message: errorMessage,
        });
    }, [trackEvent]);

    const trackError = useCallback((errorType, errorMessage, context = {}) => {
        trackEvent('error_occurred', {
            error_type: errorType,
            error_message: errorMessage,
            ...context,
        });
    }, [trackEvent]);

    const trackSearch = useCallback((searchQuery, resultsCount) => {
        trackEvent('search_performed', {
            query: searchQuery,
            results_count: resultsCount,
        });
    }, [trackEvent]);

    const trackNavigation = useCallback((from, to) => {
        trackEvent('navigation', {
            from_page: from,
            to_page: to,
        });
    }, [trackEvent]);

    // ==========================================
    // Context Value
    // ==========================================

    const value = {
        // Core functions
        identify,
        alias, // NEW: For merging anonymous → authenticated profiles
        track: trackEvent,
        trackEvent, // Export as trackEvent as well
        page: trackPageView,
        reset,
        isInitialized: isReady,

        // Traffic source helpers
        getTrafficSource,
        getUtmParams,
        getAdClickIds,

        // Authentication
        trackSignupStarted,
        trackSignupFormStep,
        trackSignup,
        trackLogin,
        trackLogout,

        // WhatsApp Verification
        trackWhatsAppOTPSent,
        trackWhatsAppVerified,
        trackWhatsAppSkipped,

        // Subscriptions
        trackSubscriptionStarted,
        trackSubscriptionCancelled,
        trackSubscriptionRenewed,
        trackSubscriptionActivated,

        // Courses & Lessons
        trackCourseViewed,
        trackLessonStarted,
        trackLessonCompleted,

        // Assessments / Level Test
        trackAssessmentStarted,
        trackAssessmentCompleted,
        trackLevelTestAbandoned,

        // Conversions
        trackDemoRequested,
        trackDemoActivated,
        trackUpgradePromptShown,
        trackUpgradeButtonClicked,
        trackCheckoutStarted,
        trackPaymentCompleted,

        // Account Activation
        trackAccountActivationEmailSent,
        trackAccountActivated,

        // Trials
        trackTrialStarted,
        trackTrialEngagement,

        // Drop-off / Abandonment
        trackSignupAbandoned,
        trackFormAbandoned,
        trackCheckoutAbandoned,

        // Exit-Intent
        trackExitIntentTriggered,
        trackExitPopupShown,
        trackExitPopupConverted,
        trackExitPopupDismissed,
        trackScrollDepth,

        // Win-back
        trackWinbackTriggered,
        trackDiscountApplied,

        // B2B Partner
        trackPartnerApplicationStarted,
        trackPartnerApplicationSubmitted,

        // Re-engagement
        trackEmailSent,
        trackEmailOpened,
        trackEmailClicked,
        trackWhatsappSent,
        trackWhatsappRead,
        trackWhatsappReplied,

        // User Engagement
        trackButtonClick,
        trackFormSubmitted,
        trackError,

        // Navigation & Search
        trackSearch,
        identify,
        reset,
        getUtmParams,
        getAdClickIds
    };

    return (
        <AnalyticsContext.Provider value={value}>
            {children}
        </AnalyticsContext.Provider>
    );
};
